// What the Topics list shows, kept while the app runs and never in the URL
// (how a list looks is not something to hand to someone): the space it is
// narrowed to, the kind (all or broadcasts) and whether only unread ones.
// One store per app (made by `conversationsScreen`), one state per workspace.
// Also the space last used for a new topic, kept on this device.
import { defineDeviceSetting, useDeviceSetting } from "@trameria/ui-core";
import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
} from "react";

export interface TopicsState {
  spaceId: string | null;
  kind: "all" | "broadcast";
  unreadOnly: boolean;
}

const INITIAL: TopicsState = { spaceId: null, kind: "all", unreadOnly: false };

export interface TopicsStore {
  get(slug: string): TopicsState;
  set(slug: string, patch: Partial<TopicsState>): void;
  subscribe(listener: () => void): () => void;
}

export function createTopicsStore(): TopicsStore {
  const states = new Map<string, TopicsState>();
  const listeners = new Set<() => void>();
  return {
    get: (slug) => states.get(slug) ?? INITIAL,
    set(slug, patch) {
      states.set(slug, { ...(states.get(slug) ?? INITIAL), ...patch });
      for (const l of listeners) l();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const TopicsStoreContext = createContext<TopicsStore | null>(null);

export function useTopicsState(
  slug: string,
): [TopicsState, (patch: Partial<TopicsState>) => void] {
  const store = useContext(TopicsStoreContext);
  if (!store)
    throw new Error("useTopicsState outside the conversation screens");
  const state = useSyncExternalStore(
    store.subscribe,
    () => store.get(slug),
    () => store.get(slug),
  );
  const set = useCallback(
    (patch: Partial<TopicsState>) => store.set(slug, patch),
    [store, slug],
  );
  return [state, set];
}

const lastSpaceSetting = defineDeviceSetting<Readonly<Record<string, string>>>({
  key: "trameria-last-topic-space",
  parse: (raw) => {
    try {
      const v: unknown = raw ? JSON.parse(raw) : {};
      return v && typeof v === "object" ? (v as Record<string, string>) : {};
    } catch {
      return {};
    }
  },
  serialize: (v) => JSON.stringify(v),
});

/** The space a new topic was last made in, in this workspace. */
export function useLastSpace(
  slug: string,
): [string | null, (spaceId: string) => void] {
  const [all, setAll] = useDeviceSetting(lastSpaceSetting);
  const set = useCallback(
    (spaceId: string) =>
      setAll({ ...lastSpaceSetting.read(), [slug]: spaceId }),
    [slug, setAll],
  );
  return [all[slug] ?? null, set];
}
